import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import supabase from '../../supabase/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Inactivity timeout configuration
const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const WARNING_TIME = 2 * 60 * 1000; // 2 minutes before logout
const SESSION_CHECK_TIMEOUT = 30 * 1000; // 30 seconds max to check session (increased from 10s)

// Superadmin role permissions mapping
const SUPERADMIN_PERMISSIONS = {
  platform_owner: ['*'], // Full access
  platform_admin: ['tenants:read', 'tenants:write', 'users:read', 'users:write', 'metrics:read', 'audit:read'],
  subscription_admin: ['tenants:read', 'subscriptions:read', 'subscriptions:write', 'metrics:read', 'audit:read'],
  support_admin: ['tenants:read', 'users:read', 'metrics:read', 'audit:read']
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'admin' or 'user'
  const [loading, setLoading] = useState(true);
  const [tenantError, setTenantError] = useState(null);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [timeUntilLogout, setTimeUntilLogout] = useState(0);

  // Superadmin state
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [superadminRole, setSuperadminRole] = useState(null);

  // Refs for inactivity timers
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // Clear all inactivity timers
  const clearInactivityTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setShowInactivityWarning(false);
    setTimeUntilLogout(0);
  }, []);

  // Inactivity logout handler (will call signOut which is defined later)
  const handleInactivityLogout = useCallback(async () => {
    console.log('🔒 Auto-logout due to inactivity');
    clearInactivityTimers();
    // Call signOut through supabase directly to avoid circular dependency
    try {
      await supabase.auth.signOut();
      setUser(null);
      setTenant(null);
      setTenantError(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [clearInactivityTimers]);

  // Reset inactivity timer on user activity
  const resetInactivityTimer = useCallback(() => {
    if (!user) return; // Only track when logged in

    // Clear existing timers
    clearInactivityTimers();

    // Set warning timer (13 minutes - 2 minutes before logout)
    warningTimerRef.current = setTimeout(() => {
      setShowInactivityWarning(true);
      setTimeUntilLogout(WARNING_TIME / 1000); // Convert to seconds

      // Start countdown
      countdownIntervalRef.current = setInterval(() => {
        setTimeUntilLogout(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, INACTIVITY_TIMEOUT - WARNING_TIME);

    // Set logout timer (15 minutes)
    inactivityTimerRef.current = setTimeout(() => {
      handleInactivityLogout();
    }, INACTIVITY_TIMEOUT);
  }, [user, handleInactivityLogout, clearInactivityTimers]);

  // Extend session by 15 more minutes
  const extendSession = useCallback(() => {
    console.log('⏰ Session extended by 15 minutes');
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  useEffect(() => {
    // Check active session
    checkUser();

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        setUser(session.user);

        // Check for superadmin claims - skip tenant loading for superadmins
        const appMetadata = session.user?.app_metadata || {};
        const isSuperadminUser = appMetadata.is_superadmin === true;
        setIsSuperadmin(isSuperadminUser);
        setSuperadminRole(appMetadata.superadmin_role || null);

        if (isSuperadminUser) {
          console.log('✅ Superadmin detected (auth change):', appMetadata.superadmin_role);
          setLoading(false);
          return;
        }

        await loadTenant(session.user.id);
      } else {
        setUser(null);
        setTenant(null);
        setIsSuperadmin(false);
        setSuperadminRole(null);
      }
      setLoading(false);
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  // Track user activity for inactivity logout
  useEffect(() => {
    if (!user) return; // Only track when logged in

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, resetInactivityTimer, { passive: true });
    });

    // Start initial timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, resetInactivityTimer);
      });
      clearInactivityTimers();
    };
  }, [user, resetInactivityTimer, clearInactivityTimers]);

  // Helper to clear session and stop loading
  const clearSessionAndFinish = useCallback(async (reason) => {
    console.log(`🔒 Clearing session: ${reason}`);
    try {
      // Clear Supabase session
      await supabase.auth.signOut();
    } catch (e) {
      // Ignore signOut errors, just clear local state
      console.warn('SignOut during clear failed (expected if offline):', e.message);
    }
    // Clear any stale localStorage keys manually as fallback
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (e) {
      console.warn('Failed to clear localStorage:', e);
    }
    setUser(null);
    setTenant(null);
    setTenantError(null);
    setLoading(false);
  }, []);

  const checkUser = async () => {
    // Create a timeout promise to prevent hanging forever
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Session check timed out')), SESSION_CHECK_TIMEOUT);
    });

    try {
      // Race between session check and timeout
      const sessionResult = await Promise.race([
        supabase.auth.getSession(),
        timeoutPromise
      ]);

      const { data: { session }, error: sessionError } = sessionResult;

      if (sessionError) {
        console.error('Session error:', sessionError);
        // Only clear session if we don't have a user locally
        // This prevents logout on transient errors if we're already logged in
        if (!user) {
          await clearSessionAndFinish('Session error');
        } else {
          console.warn('⚠️ Session check failed but keeping local session active');
          setLoading(false);
        }
        return;
      }

      if (!session?.user) {
        // No session, just finish loading (user will see login)
        setLoading(false);
        return;
      }

      // Check if token is already expired client-side
      const expiresAt = session.expires_at * 1000;
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;

      if (timeUntilExpiry <= 0) {
        // Token already expired, clear and redirect to login
        console.log('🔒 Token expired, clearing session');
        await clearSessionAndFinish('Token expired');
        return;
      }

      // Token expiring soon (< 5 minutes), try to refresh
      if (timeUntilExpiry < 5 * 60 * 1000) {
        console.log('🔄 Token expiring soon, refreshing proactively...');
        try {
          const refreshResult = await Promise.race([
            supabase.auth.refreshSession(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Refresh timed out')), 5000))
          ]);

          const { data: refreshData, error: refreshError } = refreshResult;

          if (refreshError) {
            console.error('Token refresh failed:', refreshError);
            // If refresh fails, clear session - token is about to expire anyway
            await clearSessionAndFinish('Token refresh failed');
            return;
          }

          if (refreshData?.session) {
            console.log('✅ Token refreshed successfully');
            setUser(refreshData.session.user);
            await loadTenant(refreshData.session.user.id);
            return;
          }
        } catch (refreshErr) {
          console.error('Token refresh error:', refreshErr);
          // Don't logout immediately on refresh error - wait for actual expiry
          // The Supabase client has its own auto-refresh logic that might succeed later
          console.warn('⚠️ Manual token refresh failed, but session might still be valid');
          setLoading(false);
          return;
        }
      }

      // Valid session, load user and tenant
      setUser(session.user);

      // Check for superadmin claims
      const appMetadata = session.user?.app_metadata || {};
      const isSuperadminUser = appMetadata.is_superadmin === true;
      setIsSuperadmin(isSuperadminUser);
      setSuperadminRole(appMetadata.superadmin_role || null);

      // Superadmins don't have tenant membership - they operate at platform level
      if (isSuperadminUser) {
        console.log('✅ Superadmin detected:', appMetadata.superadmin_role);
        setLoading(false);
        return;
      }

      await loadTenant(session.user.id);

    } catch (error) {
      console.error('Error checking user:', error);

      if (error.message === 'Session check timed out') {
        // Timed out - likely offline or network issue
        console.warn('⏱️ Session check timed out');

        if (!user) {
          // If we don't have a user, we can't assume they are logged in
          await clearSessionAndFinish('Session check timed out');
        } else {
          // If we DO have a user, assume they are still logged in and it's just a network blip
          console.log('✅ Keeping existing session active despite timeout');
          setLoading(false);
        }
      } else {
        // Other error
        console.error('Unexpected error during session check:', error);
        if (!user) {
          await clearSessionAndFinish('Unexpected error during session check');
        } else {
          setLoading(false);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTenant = async (userId, retryCount = 0) => {
    const MAX_RETRIES = 2; // Reduced retries to fail faster
    const RETRY_DELAYS = [1000, 2000]; // Exponential backoff in ms
    const TENANT_LOAD_TIMEOUT = 8000; // 8 seconds max per attempt

    try {
      // Race between tenant load and timeout
      const result = await Promise.race([
        supabase
          .from('tenant_members')
          .select('role, tenants(id, name, slug)')
          .eq('user_id', userId)
          .single(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Tenant load timed out')), TENANT_LOAD_TIMEOUT)
        )
      ]);

      const { data, error } = result;

      if (error) throw error;

      if (data?.tenants) {
        setTenant(data.tenants);
        setUserRole(data.role || 'user'); // Default to 'user' if not set
        setTenantError(null); // Clear any previous errors
        console.log('✅ Tenant loaded successfully:', data.tenants.name, '| Role:', data.role);
      } else {
        throw new Error('No tenant membership found');
      }
    } catch (error) {
      console.error(`❌ Error loading tenant (attempt ${retryCount + 1}/${MAX_RETRIES + 1}):`, error);

      if (retryCount < MAX_RETRIES) {
        // Retry with exponential backoff
        const delay = RETRY_DELAYS[retryCount];
        console.log(`⏳ Retrying in ${delay}ms...`);

        setTimeout(() => {
          loadTenant(userId, retryCount + 1);
        }, delay);
      } else {
        // Max retries exceeded - show error but don't hang
        console.error('❌ Max retries exceeded for loading tenant');
        setTenantError('Unable to load your organization. Please check your connection and try again.');
        setLoading(false); // Ensure loading stops
      }
    }
  };

  const signOut = async () => {
    try {
      clearInactivityTimers();
      await supabase.auth.signOut();
      setUser(null);
      setTenant(null);
      setUserRole(null);
      setTenantError(null);
      setIsSuperadmin(false);
      setSuperadminRole(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Helper to check if user is admin
  const isAdmin = userRole === 'admin';

  // Helper to check superadmin permissions
  const hasSuperadminPermission = useCallback((permission) => {
    if (!isSuperadmin || !superadminRole) return false;

    const perms = SUPERADMIN_PERMISSIONS[superadminRole] || [];
    return perms.includes('*') || perms.includes(permission);
  }, [isSuperadmin, superadminRole]);

  // Manual retry function for tenant loading
  const retryLoadTenant = useCallback(() => {
    if (user?.id) {
      setTenantError(null);
      loadTenant(user.id);
    }
  }, [user]);

  const value = {
    user,
    tenant,
    userRole,
    isAdmin,
    loading,
    tenantError,
    showInactivityWarning,
    timeUntilLogout,
    signOut,
    retryLoadTenant,
    extendSession,
    // Superadmin properties
    isSuperadmin,
    superadminRole,
    hasSuperadminPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;